import React from 'react'
import { Popup } from 'semantic-ui-react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Label,
  LabelList,
  ResponsiveContainer,
  XAxis,
  YAxis
} from 'recharts'
import dayjs from 'dayjs'
import { formatNumber } from '~/utils/helpers'
import { WidgetCard, WidgetTitle } from '../../style'
import {
  QualityIconsContainer,
  QualityIconItem,
  QualityValue,
  QualityIcon,
  QualityLabel
} from './style'
import { MilkQualityChartDataPoint } from '../../mockData'
import { useMilkMoney } from '../../MilkMoneyContext'
import { MilkQualityWidgetSkeleton } from '../../Skeletons'
import { Sample } from '~/api/services/dashboard'

import MilkFatProteinIcon from '@assets/svgs/milk-fat-protein.svg'
import CellsIcon from '@assets/svgs/cells.svg'
import GermsIcon from '@assets/svgs/germs.svg'
import { useTranslation } from 'react-i18next'

type QualityType = 'fett' | 'eiweiss' | 'zellzahl' | 'keimzahl'

interface ChartConfig {
  title: string
  unit: string
  data: MilkQualityChartDataPoint[]
}

const MilchqualitaetWidget: React.FC = () => {
  const { t } = useTranslation()
  const { milkMoneyData, isLoading } = useMilkMoney()

  if (isLoading) return <MilkQualityWidgetSkeleton />

  const samples = milkMoneyData?.samples ?? []

  if (!samples.length) {
    return (
      <WidgetCard>
        <WidgetTitle>{t('Milk quality')}</WidgetTitle>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', color: '#999', fontSize: '14px' }}>
          {t('No data available')}
        </div>
      </WidgetCard>
    )
  }

  function buildChartData(key: keyof Sample): MilkQualityChartDataPoint[] {
    return samples.map(sample => {
      const value = Number(sample[key])
      return { date: dayjs(sample.date).format('DD.MM.YYYY'), value: value }
    })
  }

  function calcAvg(key: keyof Sample): number {
    const values = samples.map(s => Number(s[key]))
    return values.reduce((a, b) => a + b, 0) / values.length
  }

  const chartConfigs: Record<QualityType, ChartConfig> = {
    fett: {
      title: t('Fat'),
      unit: '%',
      data: buildChartData('fat')
    },
    eiweiss: {
      title: t('Protein'),
      unit: '%',
      data: buildChartData('protein')
    },
    zellzahl: {
      title: t('Cell count'),
      unit: '1`000/ml',
      data: buildChartData('cellCount')
    },
    keimzahl: {
      title: t('Germ count'),
      unit: '1`000/ml',
      data: buildChartData('germCount')
    }
  }

  const qualityItems: { type: QualityType; icon: string; value: number }[] = [
    { type: 'fett', icon: MilkFatProteinIcon, value: calcAvg('fat') },
    { type: 'eiweiss', icon: MilkFatProteinIcon, value: calcAvg('protein') },
    { type: 'zellzahl', icon: CellsIcon, value: calcAvg('cellCount') },
    { type: 'keimzahl', icon: GermsIcon, value: calcAvg('germCount') }
  ]

  function formatValue(type: QualityType, value: number): string {
    const config = chartConfigs[type]
    if (type === 'zellzahl' || type === 'keimzahl') {
      return `${formatNumber(value * 1000, 0)}`
    }
    return `${formatNumber(value, 2)} ${config.unit}`
  }

  function renderChart(type: QualityType) {
    const config = chartConfigs[type]
    const min = Math.min(...config.data.map(qualityItem => qualityItem.value))
    const max = Math.max(...config.data.map(qualityItem => qualityItem.value))
    const isLargeNumber = type === 'zellzahl' || type === 'keimzahl'
    const dividerSplitRange = isLargeNumber ? 2 : 0.5
    const decimalScale = isLargeNumber ? 0 : 2
    const divider = Math.pow(10, Math.floor(min).toString().length - 1) * dividerSplitRange

    return (
      <ResponsiveContainer width='100%' height={300} minWidth={500}>
        <BarChart data={config.data} margin={{ top: 40, right: 20, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray='3 3' vertical={false} />
          <XAxis dataKey='date' tick={{ fontSize: 10 }} angle={-45} textAnchor='end' height={60} />
          <YAxis
            domain={[min - divider, max + divider]}
            tickFormatter={(value) => (`${formatNumber(value, decimalScale)}`)}
          >
            <Label position='insideTopLeft' dy={-35} dx={isLargeNumber ? 0 : 35} value={t('in {{unit}}', { unit: config.unit })} />
          </YAxis>
          {/* {config.upperLimit && <ReferenceLine y={config.upperLimit} stroke='red' strokeDasharray='5 5' overlineThickness={8} isFront={true} />}
          {config.lowerLimit && <ReferenceLine y={config.lowerLimit} stroke='red' strokeDasharray='5 5' overlineThickness={8} isFront />} */}
          <Bar dataKey='value' radius={[4, 4, 0, 0]} maxBarSize={40}>
            {config.data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={'#8BC34A'} />
            ))}
            <LabelList
              dataKey='value'
              position='top'
              content={(props: any) => {
                const { x, y, width, value } = props

                if (value == null) return null

                const label = `${formatNumber(value, decimalScale)} ${config.unit}`

                return (
                  <text
                    x={x + width / 2}
                    y={y - 8}
                    textAnchor='middle'
                    fontSize={13}
                    fill='#7aa33d'
                  >
                    {label}
                  </text>
                )
              }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    )
  }

  return (
    <WidgetCard>
      <WidgetTitle>{t('Milk quality')}</WidgetTitle>
      <QualityIconsContainer>
        {qualityItems.map((item) => {
          const { type, value } = item
          return <Popup
            key={type}
            content={renderChart(type)}
            trigger={
              <QualityIconItem key={type}>
                <QualityValue>{formatValue(type, value)}</QualityValue>
                <QualityIcon>
                  <img src={item.icon} alt={chartConfigs[type].title} />
                </QualityIcon>
                <QualityLabel>{chartConfigs[type].title}</QualityLabel>
              </QualityIconItem>
            }
          />
        })}
      </QualityIconsContainer>
    </WidgetCard>
  )
}

export default MilchqualitaetWidget
